import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import Navigation from "@/components/Navigation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Download,
  FileText,
  Image as ImageIcon,
  Palette,
  MessageSquare,
  Link as LinkIcon,
  BookOpen,
  Scale,
  Search,
  Filter,
  Grid,
  List,
  ChevronRight,
  Mail,
  HelpCircle,
  Users,
  Award,
  Calendar,
  Target,
  FileDown,
  Eye,
  Copy,
  Package,
  Camera,
  Layout,
  Type,
  Hash,
  Heart,
  Sparkles,
  Star,
  CheckCircle,
  AlertCircle,
  X,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  ExternalLink,
  Briefcase,
  GraduationCap,
  Shield,
  DollarSign,
  FileSignature,
  Book,
  Megaphone,
  BarChart3,
  Video,
  PenTool,
  Share2,
  TrendingUp,
  Zap,
  ArrowRight
} from "lucide-react";

interface MediaAsset {
  id: string;
  name: string;
  type: 'logo' | 'product' | 'template' | 'sample';
  category?: string;
  url: string;
  thumbnail?: string;
  format: string;
  size?: string;
  description?: string;
}

interface InfluencerData {
  isApproved: boolean;
  referralCode: string;
}

const brandColors = [
  { name: "Primary Purple", hex: "#8B5CF6", rgb: "139, 92, 246" },
  { name: "Secondary Pink", hex: "#EC4899", rgb: "236, 72, 153" },
  { name: "Accent Blue", hex: "#3B82F6", rgb: "59, 130, 246" },
  { name: "Success Green", hex: "#10B981", rgb: "16, 185, 129" },
  { name: "Warning Orange", hex: "#F59E0B", rgb: "245, 158, 11" },
  { name: "Neutral Gray", hex: "#6B7280", rgb: "107, 114, 128" }
];

const fontFamilies = [
  { name: "Primary", family: "Inter", usage: "Headlines & Body Text" },
  { name: "Secondary", family: "Poppins", usage: "Buttons & CTAs" },
  { name: "Display", family: "Fredoka", usage: "Playful Headers" }
];

const approvedHashtags = [
  "#SnuggleTime",
  "#StoryMagic",
  "#CraftersKids",
  "#PersonalizedStories",
  "#BedtimeStories",
  "#ImaginationFirst",
  "#FamilyBonding",
  "#CreativeKids"
];

export default function InfluencerResources() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [selectedAsset, setSelectedAsset] = useState<MediaAsset | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [copiedText, setCopiedText] = useState("");

  // Check if user is approved influencer
  const { data: influencerData, isLoading: influencerLoading } = useQuery<InfluencerData>({
    queryKey: ['/api/influencer/dashboard'],
    enabled: !!user,
    retry: false
  });

  // Get available media assets
  const { data: mediaAssets, isLoading: assetsLoading } = useQuery<MediaAsset[]>({
    queryKey: ['/api/influencer/media-kit'],
    enabled: !!influencerData?.isApproved,
    // Mock data for demonstration
    queryFn: async () => {
      // In real app, this would fetch from API
      const mockAssets: MediaAsset[] = [
        // Logo assets
        {
          id: "logo-1",
          name: "SnuggleCrafters Primary Logo",
          type: "logo",
          category: "Primary",
          url: "/logo.svg",
          format: "SVG",
          size: "24 KB",
          description: "Main logo with wordmark"
        },
        {
          id: "logo-2",
          name: "SnuggleCrafters Icon",
          type: "logo",
          category: "Icon",
          url: "/logo-icon.png",
          format: "PNG",
          size: "48 KB",
          description: "Square icon version"
        },
        {
          id: "logo-3",
          name: "White Logo",
          type: "logo",
          category: "Variations",
          url: "/logo-white.svg",
          format: "SVG",
          size: "24 KB",
          description: "For dark backgrounds"
        },
        // Product images
        {
          id: "product-1",
          name: "Personalized Storybook",
          type: "product",
          category: "Books",
          url: "/products/storybook.jpg",
          thumbnail: "/products/storybook-thumb.jpg",
          format: "JPG",
          size: "2.4 MB",
          description: "High-res product shot"
        },
        {
          id: "product-2",
          name: "Character Collection",
          type: "product",
          category: "Characters",
          url: "/products/characters.jpg",
          thumbnail: "/products/characters-thumb.jpg",
          format: "JPG",
          size: "1.8 MB",
          description: "All character lineup"
        },
        // Marketing templates
        {
          id: "template-1",
          name: "Instagram Story Template",
          type: "template",
          category: "Social Media",
          url: "/templates/ig-story.psd",
          format: "PSD",
          size: "15 MB",
          description: "Editable Photoshop template"
        },
        {
          id: "template-2",
          name: "Facebook Post Template",
          type: "template",
          category: "Social Media",
          url: "/templates/fb-post.png",
          format: "PNG",
          size: "450 KB",
          description: "1200x630 optimized"
        },
        // Sample content
        {
          id: "sample-1",
          name: "Instagram Caption Examples",
          type: "sample",
          category: "Captions",
          url: "/samples/ig-captions.txt",
          format: "TXT",
          size: "12 KB",
          description: "20 ready-to-use captions"
        },
        {
          id: "sample-2",
          name: "Video Script Template",
          type: "sample",
          category: "Video",
          url: "/samples/video-script.docx",
          format: "DOCX",
          size: "28 KB",
          description: "TikTok & Reels script"
        }
      ];
      return mockAssets;
    }
  });

  // Track downloads
  const downloadMutation = useMutation({
    mutationFn: ({ assetId, assetType }: { assetId: string; assetType: string }) => 
      apiRequest('POST', '/api/influencer/media/download', { assetId, assetType }),
    onSuccess: () => {
      // Download tracked successfully
    }
  });

  const handleDownload = (asset: MediaAsset) => {
    // Track download
    downloadMutation.mutate({ assetId: asset.id, assetType: asset.type });
    
    // Create download link
    const link = document.createElement('a');
    link.href = asset.url;
    link.download = asset.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download Started",
      description: `Downloading ${asset.name}`,
    });
  };

  const handleDownloadAll = (type: string) => {
    const assetsToDownload = mediaAssets?.filter(a => a.type === type) || [];
    
    assetsToDownload.forEach((asset, index) => {
      setTimeout(() => {
        handleDownload(asset);
      }, index * 500); // Stagger downloads
    });
    
    toast({
      title: "Bulk Download Started",
      description: `Downloading ${assetsToDownload.length} ${type} assets`,
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(""), 2000);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
  };

  const filteredAssets = mediaAssets?.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          asset.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || asset.type === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (influencerLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    setLocation('/auth');
    return null;
  }

  if (!influencerData?.isApproved) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You need to be an approved influencer to access resources. 
              <Button
                variant="link"
                className="px-2"
                onClick={() => setLocation('/influencer')}
              >
                Go to Dashboard
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Header with Breadcrumbs */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/influencer">
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Resources & Media Kit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Briefcase className="w-8 h-8 text-purple-500" />
            Influencer Resources & Media Kit
          </h1>
          <p className="text-muted-foreground">
            Everything you need to promote SnuggleCrafters successfully
          </p>
        </div>

        {/* Quick Actions Bar */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button
            variant="default"
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/brand-guidelines.pdf';
              link.download = 'SnuggleCrafters-Brand-Guidelines.pdf';
              link.click();
            }}
          >
            <FileDown className="w-4 h-4 mr-2" />
            Download Complete Brand Guide (PDF)
          </Button>
          <Button
            variant="outline"
            onClick={() => setLocation('/influencer')}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            View Dashboard
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = `mailto:craig.ross@snugglecrafters.com?subject=Influencer Support Request`}
          >
            <Mail className="w-4 h-4 mr-2" />
            Contact Support
          </Button>
        </div>

        <Tabs defaultValue="guidelines" className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-lg">
            <TabsTrigger value="guidelines">Brand Guidelines</TabsTrigger>
            <TabsTrigger value="assets">Media Assets</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
          </TabsList>

          {/* Brand Guidelines Tab */}
          <TabsContent value="guidelines" className="space-y-6">
            {/* Brand Story */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-purple-500" />
                  Brand Story
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Mission</h4>
                  <p className="text-muted-foreground">
                    To spark imagination and strengthen family bonds through personalized storytelling experiences 
                    that make every child the hero of their own adventure.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Vision</h4>
                  <p className="text-muted-foreground">
                    A world where bedtime becomes a magical journey of discovery, learning, and connection 
                    between parents and children.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Core Values</h4>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge variant="outline">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Creativity
                    </Badge>
                    <Badge variant="outline">
                      <Users className="w-3 h-3 mr-1" />
                      Family Bonding
                    </Badge>
                    <Badge variant="outline">
                      <Star className="w-3 h-3 mr-1" />
                      Imagination
                    </Badge>
                    <Badge variant="outline">
                      <BookOpen className="w-3 h-3 mr-1" />
                      Learning
                    </Badge>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Target Audience</h4>
                  <p className="text-muted-foreground">
                    Parents and caregivers of children aged 3-12 who value quality time, educational content, 
                    and creating memorable experiences with their kids.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Visual Identity */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-purple-500" />
                  Visual Identity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Brand Colors */}
                <div>
                  <h4 className="font-semibold mb-4">Brand Colors</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {brandColors.map((color) => (
                      <div key={color.hex} className="space-y-2">
                        <div 
                          className="h-20 rounded-lg shadow-sm border"
                          style={{ backgroundColor: color.hex }}
                        />
                        <div className="space-y-1">
                          <p className="font-medium text-sm">{color.name}</p>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-muted px-1 py-0.5 rounded">
                              {color.hex}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2"
                              onClick={() => copyToClipboard(color.hex, color.name)}
                            >
                              {copiedText === color.name ? (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            RGB: {color.rgb}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Typography */}
                <div>
                  <h4 className="font-semibold mb-4">Typography</h4>
                  <div className="space-y-3">
                    {fontFamilies.map((font) => (
                      <div key={font.family} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium" style={{ fontFamily: font.family }}>
                            {font.name}: {font.family}
                          </p>
                          <p className="text-sm text-muted-foreground">{font.usage}</p>
                        </div>
                        <Badge variant="outline">{font.name}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Logo Usage Rules */}
                <div>
                  <h4 className="font-semibold mb-4">Logo Usage Rules</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Do's
                      </h5>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Maintain minimum size of 120px width</li>
                        <li>• Keep clear space equal to 'x' height</li>
                        <li>• Use on contrasting backgrounds</li>
                        <li>• Use provided logo files only</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-red-600 flex items-center gap-1">
                        <X className="w-4 h-4" />
                        Don'ts
                      </h5>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Don't stretch or distort</li>
                        <li>• Don't change colors</li>
                        <li>• Don't add effects or shadows</li>
                        <li>• Don't recreate or modify</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Messaging Guidelines */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-500" />
                  Messaging Guidelines
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Key Messages */}
                <div>
                  <h4 className="font-semibold mb-3">Key Messages & Talking Points</h4>
                  <div className="space-y-2">
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm">
                        "Every child deserves to be the hero of their own story - SnuggleCrafters makes it happen with personalized adventures."
                      </p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm">
                        "Transform bedtime into quality bonding time with stories tailored to your child's interests and imagination."
                      </p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm">
                        "Educational storytelling that grows with your child - from simple tales to complex adventures."
                      </p>
                    </div>
                  </div>
                </div>

                {/* Approved Hashtags */}
                <div>
                  <h4 className="font-semibold mb-3">Approved Hashtags</h4>
                  <div className="flex flex-wrap gap-2">
                    {approvedHashtags.map((tag) => (
                      <Button
                        key={tag}
                        variant="outline"
                        size="sm"
                        className="text-purple-600"
                        onClick={() => copyToClipboard(tag, tag)}
                      >
                        <Hash className="w-3 h-3 mr-1" />
                        {tag.slice(1)}
                        {copiedText === tag && (
                          <CheckCircle className="w-3 h-3 ml-2 text-green-500" />
                        )}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Content Creation Guidelines */}
                <div>
                  <h4 className="font-semibold mb-3">Content Creation Guidelines</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Do's
                      </h5>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Share authentic experiences</li>
                        <li>• Focus on family moments</li>
                        <li>• Highlight personalization features</li>
                        <li>• Show real reactions from kids</li>
                        <li>• Use bright, warm imagery</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-red-600 flex items-center gap-1">
                        <X className="w-4 h-4" />
                        Don'ts
                      </h5>
                      <ul className="text-sm space-y-1 text-muted-foreground">
                        <li>• Make false claims</li>
                        <li>• Compare negatively to competitors</li>
                        <li>• Use inappropriate language</li>
                        <li>• Share private customer data</li>
                        <li>• Forget FTC disclosures</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* FTC Disclosure */}
                <div>
                  <h4 className="font-semibold mb-3">FTC Disclosure Requirements</h4>
                  <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950">
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-medium mb-2">Required Disclosure Text:</p>
                      <div className="p-2 bg-white dark:bg-gray-900 rounded border text-sm">
                        "I'm a SnuggleCrafters affiliate and earn commission from qualifying purchases. 
                        #ad #sponsored"
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => copyToClipboard(
                          "I'm a SnuggleCrafters affiliate and earn commission from qualifying purchases. #ad #sponsored",
                          "FTC Disclosure"
                        )}
                      >
                        <Copy className="w-3 h-3 mr-2" />
                        Copy Disclosure
                      </Button>
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Media Assets Tab */}
          <TabsContent value="assets" className="space-y-6">
            {/* Search and Filter Bar */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search assets..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <select
                  className="px-3 py-2 border rounded-md"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="logo">Logos</option>
                  <option value="product">Product Images</option>
                  <option value="template">Templates</option>
                  <option value="sample">Sample Content</option>
                </select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
                >
                  {viewMode === "grid" ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Logo Package */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-purple-500" />
                    Logo Package
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadAll('logo')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All
                  </Button>
                </div>
                <CardDescription>
                  Official SnuggleCrafters logos in various formats
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={viewMode === "grid" ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" : "space-y-2"}>
                  {filteredAssets?.filter(a => a.type === 'logo').map((asset) => (
                    <div
                      key={asset.id}
                      className={viewMode === "grid" 
                        ? "border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                        : "flex items-center justify-between p-3 border rounded-lg hover:bg-muted transition-colors"
                      }
                    >
                      <div className={viewMode === "grid" ? "space-y-2" : "flex items-center gap-3"}>
                        <div className={viewMode === "grid" ? "h-20 bg-muted rounded flex items-center justify-center" : ""}>
                          <ImageIcon className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{asset.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="secondary">{asset.format}</Badge>
                            <span>{asset.size}</span>
                          </div>
                        </div>
                      </div>
                      <div className={viewMode === "grid" ? "flex gap-1 mt-2" : "flex gap-2"}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setSelectedAsset(asset)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownload(asset)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Product Images */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-purple-500" />
                    Product Images
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadAll('product')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All
                  </Button>
                </div>
                <CardDescription>
                  High-resolution product photography for your content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={viewMode === "grid" ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" : "space-y-2"}>
                  {filteredAssets?.filter(a => a.type === 'product').map((asset) => (
                    <div
                      key={asset.id}
                      className={viewMode === "grid" 
                        ? "border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                        : "flex items-center justify-between p-3 border rounded-lg hover:bg-muted transition-colors"
                      }
                    >
                      {viewMode === "grid" ? (
                        <>
                          <div className="h-32 bg-muted flex items-center justify-center">
                            <ImageIcon className="w-12 h-12 text-muted-foreground" />
                          </div>
                          <div className="p-3">
                            <p className="font-medium text-sm">{asset.name}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {asset.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary">{asset.format}</Badge>
                              <span className="text-xs">{asset.size}</span>
                            </div>
                            <div className="flex gap-1 mt-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setSelectedAsset(asset)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDownload(asset)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-3">
                            <ImageIcon className="w-8 h-8 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-sm">{asset.name}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Badge variant="secondary">{asset.format}</Badge>
                                <span>{asset.size}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setSelectedAsset(asset)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDownload(asset)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Marketing Templates */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Layout className="w-5 h-5 text-purple-500" />
                    Marketing Templates
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadAll('template')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All
                  </Button>
                </div>
                <CardDescription>
                  Ready-to-use templates for social media and marketing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded">
                          <Instagram className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium mb-1">Instagram Templates</h5>
                          <p className="text-xs text-muted-foreground mb-2">
                            Stories, Posts, Reels covers
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open('https://canva.com/templates/instagram', '_blank')}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Canva
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload({ 
                                id: 'ig-templates', 
                                name: 'Instagram Templates', 
                                type: 'template',
                                url: '/templates/instagram.zip',
                                format: 'ZIP'
                              } as MediaAsset)}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              PSD
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded">
                          <Facebook className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium mb-1">Facebook Templates</h5>
                          <p className="text-xs text-muted-foreground mb-2">
                            Posts, Cover photos, Ads
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open('https://canva.com/templates/facebook', '_blank')}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Canva
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload({ 
                                id: 'fb-templates', 
                                name: 'Facebook Templates', 
                                type: 'template',
                                url: '/templates/facebook.zip',
                                format: 'ZIP'
                              } as MediaAsset)}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              PNG
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-red-100 dark:bg-red-900 rounded">
                          <Video className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium mb-1">TikTok Templates</h5>
                          <p className="text-xs text-muted-foreground mb-2">
                            Video covers, Thumbnails
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open('https://canva.com/templates/tiktok', '_blank')}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Canva
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload({ 
                                id: 'tiktok-templates', 
                                name: 'TikTok Templates', 
                                type: 'template',
                                url: '/templates/tiktok.zip',
                                format: 'ZIP'
                              } as MediaAsset)}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Files
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-gray-100 dark:bg-gray-900 rounded">
                          <Mail className="w-5 h-5 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium mb-1">Email Signatures</h5>
                          <p className="text-xs text-muted-foreground mb-2">
                            Professional email signatures
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload({ 
                              id: 'email-sig', 
                              name: 'Email Signatures', 
                              type: 'template',
                              url: '/templates/email-signatures.html',
                              format: 'HTML'
                            } as MediaAsset)}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded">
                          <Layout className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium mb-1">Banner Ads</h5>
                          <p className="text-xs text-muted-foreground mb-2">
                            Various sizes for web display
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload({ 
                              id: 'banners', 
                              name: 'Banner Ads', 
                              type: 'template',
                              url: '/templates/banners.zip',
                              format: 'ZIP'
                            } as MediaAsset)}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            All Sizes
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-900 rounded">
                          <Youtube className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium mb-1">YouTube Templates</h5>
                          <p className="text-xs text-muted-foreground mb-2">
                            Thumbnails, End screens
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload({ 
                              id: 'youtube', 
                              name: 'YouTube Templates', 
                              type: 'template',
                              url: '/templates/youtube.zip',
                              format: 'ZIP'
                            } as MediaAsset)}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* Sample Content */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <PenTool className="w-5 h-5 text-purple-500" />
                    Sample Content
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadAll('sample')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All
                  </Button>
                </div>
                <CardDescription>
                  Example posts, captions, and scripts to inspire your content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredAssets?.filter(a => a.type === 'sample').map((asset) => (
                    <div key={asset.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium mb-1">{asset.name}</h5>
                          <p className="text-sm text-muted-foreground mb-2">
                            {asset.description}
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{asset.format}</Badge>
                            <span className="text-xs text-muted-foreground">{asset.size}</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setSelectedAsset(asset)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDownload(asset)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-6">
            {/* Quick Links */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-500" />
                  Quick Links
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => setLocation('/influencer')}
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Referral Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => {
                      const referralUrl = `${window.location.origin}?ref=${influencerData?.referralCode}`;
                      copyToClipboard(referralUrl, "Referral Link");
                    }}
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Copy Referral Link
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => window.location.href = `mailto:craig.ross@snugglecrafters.com?subject=Influencer Support`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Craig Ross
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => setLocation('/help')}
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    FAQ & Support
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Training Materials */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-purple-500" />
                  Training Materials
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium mb-1">How to Maximize Conversions</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          Learn proven strategies to increase your referral conversion rates
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload({ 
                            id: 'conversion-guide', 
                            name: 'Conversion Guide', 
                            type: 'sample',
                            url: '/guides/maximize-conversions.pdf',
                            format: 'PDF'
                          } as MediaAsset)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Guide
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded">
                        <Share2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium mb-1">Social Media Best Practices</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          Platform-specific tips for Instagram, Facebook, TikTok, and more
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload({ 
                            id: 'social-guide', 
                            name: 'Social Media Guide', 
                            type: 'sample',
                            url: '/guides/social-media-best-practices.pdf',
                            format: 'PDF'
                          } as MediaAsset)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Guide
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900 rounded">
                        <Calendar className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium mb-1">Content Calendar Template</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          Plan your promotional content with our monthly calendar template
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload({ 
                            id: 'calendar', 
                            name: 'Content Calendar', 
                            type: 'sample',
                            url: '/templates/content-calendar.xlsx',
                            format: 'XLSX'
                          } as MediaAsset)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Template
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded">
                        <Sparkles className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium mb-1">Seasonal Campaign Ideas</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          Holiday and seasonal promotion strategies throughout the year
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload({ 
                            id: 'seasonal', 
                            name: 'Seasonal Campaigns', 
                            type: 'sample',
                            url: '/guides/seasonal-campaigns.pdf',
                            format: 'PDF'
                          } as MediaAsset)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Ideas
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Legal & Compliance */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-500" />
                  Legal & Compliance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <FileSignature className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1">
                        <h5 className="font-medium mb-1">Influencer Agreement</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          Review the terms of your influencer partnership
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload({ 
                            id: 'agreement', 
                            name: 'Influencer Agreement', 
                            type: 'sample',
                            url: '/legal/influencer-agreement.pdf',
                            format: 'PDF'
                          } as MediaAsset)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Agreement
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1">
                        <h5 className="font-medium mb-1">FTC Guidelines</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          Federal Trade Commission disclosure requirements
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open('https://www.ftc.gov/business-guidance/resources/disclosures-101-social-media-influencers', '_blank')}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View Guidelines
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <DollarSign className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1">
                        <h5 className="font-medium mb-1">Tax Information</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          1099 contractor tax guidelines and resources
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload({ 
                            id: 'tax-info', 
                            name: 'Tax Information', 
                            type: 'sample',
                            url: '/legal/tax-info-1099.pdf',
                            format: 'PDF'
                          } as MediaAsset)}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Info
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Book className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1">
                        <h5 className="font-medium mb-1">Terms of Service</h5>
                        <p className="text-sm text-muted-foreground mb-2">
                          SnuggleCrafters platform terms and conditions
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setLocation('/terms')}
                        >
                          <ArrowRight className="w-3 h-3 mr-1" />
                          View Terms
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Asset Preview Modal */}
      <Dialog open={!!selectedAsset} onOpenChange={() => setSelectedAsset(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedAsset?.name}</DialogTitle>
            <DialogDescription>
              {selectedAsset?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-8 flex items-center justify-center min-h-[200px]">
              <ImageIcon className="w-16 h-16 text-muted-foreground" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedAsset?.format}</Badge>
                <span className="text-sm text-muted-foreground">{selectedAsset?.size}</span>
              </div>
              <Button
                onClick={() => {
                  if (selectedAsset) {
                    handleDownload(selectedAsset);
                    setSelectedAsset(null);
                  }
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}